<template>
  <div>
    <div
      v-for="(setParams, setIndex) in sets"
      :key="'set'+setIndex"
      class="align-center mb-2"
    >
      <div
        class="grey--text text--darken-1 d-inline-block"
        style="width: 50px"
        :class="{ 'caption': dense, 'text-body-1': !dense }"
      >
        {{ $t("training_builder.configure.set", {set: setIndex + 1}) }}:
      </div>

      <exercise-param-label-helper
        :label="$t('reps.label')"
        :dense="dense"
        :param="setParams.NMRP"
      />

      <exercise-param-label-helper
        :label="$t('rep_work.label')"
        :dense="dense"
        :param="setParams.WRPRDR"
        suffix="s"
      />

      <exercise-param-label-helper
        :label="$t('event_type.rest')"
        :dense="dense"
        :param="setParams.RSPRDR"
        suffix="s"
      />

      <exercise-param-label-helper
        :label="$t('rest_after_set.label')"
        :dense="dense"
        :param="setParams.RSSTDR"
        suffix="s"
      />

      <exercise-param-label-helper
        :label="$t('stations.label')"
        :dense="dense"
        :param="setParams.NMST"
      />

      <exercise-param-label-helper
        :label="$t('training_builder.exercise_params.football.dim1')"
        :dense="dense"
        :param="getDistance(setParams.DMS1)"
        :suffix="distanceSuffix(user.organisation_settings.unit_system)"
      />

      <exercise-param-label-helper
        :label="$t('training_builder.exercise_params.football.dim2')"
        :dense="dense"
        :param="getDistance(setParams.DMS2)"
        :suffix="distanceSuffix(user.organisation_settings.unit_system)"
      />

      <exercise-param-label-helper
        :label="$t('player_in_station.label')"
        :dense="dense"
        :param="setParams.PLST"
      />

      <exercise-param-label-helper
        :label="$t('training_builder.dropdown.terrain.title')"
        :dense="dense"
        :param="setParams.TRRN"
      />

      <exercise-param-label-helper
        :label="$t('training_builder.dropdown.goal.title')"
        :dense="dense"
        :param="setParams.GOAL"
      />

      <exercise-param-label-helper
        :label="$t('training_builder.dropdown.contacts.title')"
        :dense="dense"
        :param="setParams.NMCNT"
      />

      <exercise-param-label-helper
        :label="$t('training_builder.dropdown.joker.title')"
        :dense="dense"
        :param="setParams.JKRNR"
        :force-value="getJoker(setParams.JKR)"
      />

      <exercise-param-label-helper
        :label="$t('effort.label')"
        :dense="dense"
        :param="setParams.EFFRT"
      />

      <v-menu
        v-if="getGroups(setParams).length > 0"
        open-on-hover
        offset-y
        :close-on-content-click="false"
      >
        <template v-slot:activator="{ on }">
          <v-chip
            :small="!dense"
            :x-small="dense"
            class="mr-1"
            label
            v-on="on"
          >
            <v-icon
              x-small
              left
            >
              fal fa-users
            </v-icon>
            {{ groupsSummaryText(getGroups(setParams)) }}
          </v-chip>
        </template>
        <v-card min-width="250">
          <template v-for="(group, gIndex) in getGroups(setParams)">
            <v-divider
              v-if="gIndex > 0"
              :key="'div' + gIndex"
            />
            <div
              :key="'group' + gIndex"
              class="px-3 py-2"
            >
              <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">
                {{ groupLabel(group, gIndex, getGroups(setParams)) }}
              </div>
              <div
                v-for="(playerId, pIndex) in (group.player_ids || [])"
                :key="'p' + playerId"
                class="caption d-flex align-center justify-space-between"
                :class="{ 'mt-1': pIndex > 0 }"
              >
                <span class="grey--text text--darken-3">{{ getPlayerName(group, playerId) }}</span>
                <v-chip
                  v-if="(group.inside_joker_ids || []).includes(playerId)"
                  x-small
                  label
                  color="blue lighten-4"
                  class="ml-4"
                >
                  {{ $t('label.inside') }}
                </v-chip>
                <v-chip
                  v-if="(group.outside_joker_ids || []).includes(playerId)"
                  x-small
                  label
                  color="orange lighten-4"
                  class="ml-4"
                >
                  {{ $t('label.outside') }}
                </v-chip>
              </div>
            </div>
          </template>
        </v-card>
      </v-menu>
    </div>
  </div>
</template>
<script>

import ExerciseParamLabelHelper from '@/components/training-builder/ExerciseParamLabelHelper.vue'
import { convertDistanceByUnitSystem, distanceSuffix } from '@/components/training-builder/helpers'

export default {
  name: 'FootballParamsView',
  components: { ExerciseParamLabelHelper },
  props: {
    sets: {
      type: Array,
      required: true
    },
    dense: {
      type: Boolean,
      default: false
    },
    hasRealizations: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    }
  },
  methods: {
    distanceSuffix,
    getDistance(param) {
      if (param && typeof param === 'object' && 'def' in param) {
        return {
          def: convertDistanceByUnitSystem(param.def, this.user.organisation_settings.unit_system, true),
          real: convertDistanceByUnitSystem(param.real, this.user.organisation_settings.unit_system, true)
        }
      }

      if (!param) return null

      return convertDistanceByUnitSystem(param, this.user.organisation_settings.unit_system, true)
    },
    getJoker(param) {
      if (!param) return 'None'

      if (param && !(typeof param === 'object')) return null

      if (param.def || param.real) return null

      return 'None'
    },
    getGroups(setParams) {
      const groups = setParams.groups
      if (!groups) return []
      const arr = (typeof groups === 'object' && 'def' in groups) ? groups.def : groups
      return arr || []
    },
    getPlayerName(group, playerId) {
      const player = group.player_names?.[playerId]
      return player ? `${player.first_name} ${player.last_name}` : `#${playerId}`
    },
    groupLabel(group, index, groups) {
      if (group.type === 'inside_jokers') return this.$t('dropdown.group_type.inside_jokers')
      if (group.type === 'outside_jokers') return this.$t('dropdown.group_type.outside_jokers')

      const teamIndex = groups.filter((g, i) => g.type === 'team' && i <= index).length
      return this.$t('label.team') + ' ' + teamIndex
    },
    groupsSummaryText(groups) {
      const groupCount = groups.length
      const playerCount = groups.reduce((sum, g) => sum + (g.player_ids || []).length, 0)
      const jokerCount = groups.reduce((sum, g) => sum + (g.inside_joker_ids || []).length + (g.outside_joker_ids || []).length, 0)

      let text = `${groupCount} ${groupCount === 1 ? 'group' : 'groups'} \u00B7 ${playerCount} ${playerCount === 1 ? 'player' : 'players'}`
      if (jokerCount > 0) text += ` (${jokerCount} ${jokerCount === 1 ? 'joker' : 'jokers'})`
      return text
    }
  }
}
</script>
